import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './button';
import { AlertTriangle, Shield, X, Check, Zap } from 'lucide-react';

const LiveTradingConfirmModal = ({ isOpen, onClose, onConfirm, botName, strategy }) => {
  const [step, setStep] = useState(1);
  const [acknowledged, setAcknowledged] = useState({
    risk: false,
    funds: false,
    stop_loss: false,
  });

  const canProceed = Object.values(acknowledged).every(v => v);

  const handleConfirm = () => {
    if (step === 1 && canProceed) {
      setStep(2);
    } else if (step === 2) {
      onConfirm();
      onClose();
      setStep(1);
      setAcknowledged({ risk: false, funds: false, stop_loss: false });
    }
  };

  const handleClose = () => {
    onClose();
    setStep(1);
    setAcknowledged({ risk: false, funds: false, stop_loss: false });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#15171C] border border-[#1E293B] rounded-lg max-w-md w-full p-6"
          onClick={e => e.stopPropagation()}
        >
          {step === 1 ? (
            <>
              {/* Step 1: Risk Acknowledgement */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#EF4444]/20 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-[#EF4444]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-[#F5F7FA] font-['Outfit']">Enable Live Trading</h2>
                    <p className="text-sm text-[#94A3B8]">Please acknowledge the risks</p>
                  </div>
                </div>
                <button onClick={handleClose} className="text-[#94A3B8] hover:text-[#F5F7FA]">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Warning Banner */}
              <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg p-4 mb-6">
                <p className="text-[#EF4444] text-sm font-medium">
                  Live trading uses real funds. Losses are permanent and cannot be recovered.
                </p>
              </div>

              {/* Bot Info */}
              <div className="bg-[#0B0D12] rounded-lg p-4 mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-[#94A3B8]">Bot</span>
                  <span className="text-[#F5F7FA]">{botName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#94A3B8]">Strategy</span>
                  <span className="text-[#2F6BFF]">{strategy}</span>
                </div>
              </div>

              {/* Acknowledgement Checkboxes */}
              <div className="space-y-3 mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acknowledged.risk}
                    onChange={(e) => setAcknowledged(prev => ({ ...prev, risk: e.target.checked }))}
                    className="mt-1 w-4 h-4 rounded border-[#1E293B] bg-[#0B0D12] text-[#2F6BFF] focus:ring-[#2F6BFF]"
                  />
                  <span className="text-sm text-[#94A3B8]">
                    I understand that trading involves significant risk and I may lose some or all of my invested capital.
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acknowledged.funds}
                    onChange={(e) => setAcknowledged(prev => ({ ...prev, funds: e.target.checked }))}
                    className="mt-1 w-4 h-4 rounded border-[#1E293B] bg-[#0B0D12] text-[#2F6BFF] focus:ring-[#2F6BFF]"
                  />
                  <span className="text-sm text-[#94A3B8]">
                    I am only using funds that I can afford to lose without affecting my financial wellbeing.
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acknowledged.stop_loss}
                    onChange={(e) => setAcknowledged(prev => ({ ...prev, stop_loss: e.target.checked }))}
                    className="mt-1 w-4 h-4 rounded border-[#1E293B] bg-[#0B0D12] text-[#2F6BFF] focus:ring-[#2F6BFF]"
                  />
                  <span className="text-sm text-[#94A3B8]">
                    I have configured appropriate stop-loss settings and understand how they work.
                  </span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={handleClose}
                  variant="outline"
                  className="flex-1 border-[#1E293B] text-[#94A3B8]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={!canProceed}
                  className="flex-1 bg-[#EF4444] hover:bg-[#EF4444]/90 disabled:opacity-50"
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Step 2: Final Confirmation */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#2F6BFF]/20 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-[#2F6BFF]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-[#F5F7FA] font-['Outfit']">Final Confirmation</h2>
                    <p className="text-sm text-[#94A3B8]">This action will start real trading</p>
                  </div>
                </div>
                <button onClick={handleClose} className="text-[#94A3B8] hover:text-[#F5F7FA]">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="text-center py-6">
                <div className="w-16 h-16 bg-[#EF4444]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-[#EF4444]" />
                </div>
                <p className="text-[#F5F7FA] font-medium mb-2">
                  Are you sure you want to enable live trading?
                </p>
                <p className="text-sm text-[#94A3B8]">
                  Real orders will be placed on your connected exchange.
                </p>
              </div>

              {/* Safety Features */}
              <div className="bg-[#0B0D12] rounded-lg p-4 mb-6">
                <p className="text-xs text-[#94A3B8] mb-2">Your safety features:</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-[#22C55E]">
                    <Check className="w-4 h-4" />
                    <span>Stop-loss protection active</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#22C55E]">
                    <Check className="w-4 h-4" />
                    <span>Kill switch available</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#22C55E]">
                    <Check className="w-4 h-4" />
                    <span>Daily loss limits configured</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="flex-1 border-[#1E293B] text-[#94A3B8]"
                >
                  Go Back
                </Button>
                <Button
                  onClick={handleConfirm}
                  className="flex-1 bg-[#EF4444] hover:bg-[#EF4444]/90"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Enable Live Trading
                </Button>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LiveTradingConfirmModal;
